import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa um centro de custo
 * 
 * @typedef {Object} CentroCusto
 * @property {number} id - Identificador único
 * @property {string} codigo - Código do centro de custo
 * @property {string} nome - Nome do centro de custo
 * @property {number|null} business_unit_id - ID da business unit vinculada
 * @property {Object|null} business_unit - Business unit vinculada
 * @property {boolean} ativo - Indica se está ativo
 * @property {string|null} competencia_inicio - Data de início de vigência no formato "YYYY-MM"
 * @property {string|null} competencia_fim - Data de fim de vigência no formato "YYYY-MM"
 */

/**
 * Representa o payload de criação/atualização de um centro de custo
 * 
 * @typedef {Object} CentroCustoPayload
 * @property {string} codigo - Código (obrigatório, min 1, max 15)
 * @property {string} nome - Nome (obrigatório, max 255)
 * @property {number} business_unit_id - ID da business unit (obrigatório)
 * @property {boolean} [ativo] - Indica se está ativo
 */

const resource = {
  base: (empresaId) => `v1/ativacaoped/${empresaId}/centros-de-custo`,
  item: (empresaId, centroCustoId) => `v1/ativacaoped/${empresaId}/centros-de-custo/${centroCustoId}`
};

const LOADING_KEY = 'ativacaoped-centros-de-custo';

/**
 * Lista centros de custo com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object|string} [params] - Parâmetros de consulta (objeto) ou query string já montada
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<CentroCusto>>} Lista paginada de centros de custo
 */
export const getCentrosCusto = (empresaId, params = { page: 1, per_page: 10 }) => {
  // Aceita a query string crua montada pelo MasterDetail (page/per_page/sort/filters).
  const query = typeof params === "string" ? params : parseParams(params);
  let url = resource.base(empresaId);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista centros de custo simplificados para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<CentroCusto[]>} Lista de centros de custo
 */
export const getSelecaoCentrosCusto = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.base(empresaId).concat(`/selecao`);

  if (query) {
    url = url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data.data || data)
  );
};

/**
 * Cria um novo centro de custo.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {CentroCustoPayload} data - Dados do centro de custo a ser criado
 * @returns {Promise<CentroCusto>} Centro de custo criado
 */
export const postCentroCusto = (empresaId, data) => {
  const baseUrl = resource.base(empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Atualiza um centro de custo existente (backend usa POST para update).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} centroCustoId - ID do centro de custo a ser atualizado
 * @param {CentroCustoPayload} data - Dados parciais do centro de custo para atualização
 * @returns {Promise<CentroCusto>} Centro de custo atualizado
 */
export const putCentroCusto = (empresaId, centroCustoId, data) => {
  const baseUrl = resource.item(empresaId, centroCustoId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
};

/**
 * Exclui um centro de custo.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} centroCustoId - ID do centro de custo a ser excluído
 * @returns {Promise<void>}
 */
export const deleteCentroCusto = (empresaId, centroCustoId) => {
  const baseUrl = resource.item(empresaId, centroCustoId);

  return withLoading(LOADING_KEY, () =>
    api.delete(baseUrl).then(({ data }) => data)
  );
};

export default {
  getCentrosCusto,
  getSelecaoCentrosCusto,
  postCentroCusto,
  putCentroCusto,
  deleteCentroCusto
};